import { SuretyAccountStatus } from "@inrev/common";
import { BondRequest } from "@portal/api/bond-request/agent";
import type { ReactNode } from "react";
import { cn } from "../lib/utils";
import { Icon, IconType } from "../primitives";

export type StatusCardType = BondRequest["status"] | SuretyAccountStatus;

export type StatusCardCoreProps = {
	type: StatusCardType;
	className?: string;
	label: string;
	iconOnly?: boolean;
	/**
	 * Rendered inside the pill after the label. Use for a disclosure chevron so the affordance sits
	 * within the pill's border rather than floating beside it.
	 */
	trailing?: ReactNode;
	/** When false, the label keeps its full width instead of ellipsizing. */
	truncateLabel?: boolean;
};

const textColorMap = {
	pending: "text-[#111835]",
	review: "text-intent-warning-text",
	approved: "text-intent-success-text",
	accepted: "text-teal-700",
	closed: "text-intent-neutral-text",
	declined: "text-intent-danger-text",
	active: "text-intent-info-text",
	bound: undefined,
};

const bgColorMap = {
	pending: "bg-white",
	review: "bg-intent-warning-bg",
	approved: "bg-intent-success-bg",
	accepted: "bg-white",
	closed: "bg-white",
	declined: "bg-white",
	active: "bg-white",
	bound: undefined,
};

const borderMap = {
	pending: "border border-[#111835]",
	review: "border border-intent-warning-text",
	approved: "border border-intent-success-text",
	accepted: "border border-teal-700",
	closed: "border border-intent-neutral-text",
	declined: "border border-intent-danger-text",
	active: "border border-intent-info-text",
	bound: undefined,
};

const iconMap: Record<StatusCardType, IconType | undefined> = {
	pending: "clock",
	review: "exclamation-triangle",
	approved: "check",
	accepted: "check-circle",
	closed: "minus-circle",
	declined: "x-circle",
	active: "bolt",
	bound: undefined,
};

// Testing only
export const StatusCard = ({
	type,
	label,
	className,
	iconOnly,
	trailing,
	truncateLabel = true,
}: StatusCardCoreProps) => {
	const iconType = iconMap[type];

	return (
		<div
			data-testid="status-card"
			className={cn(
				`flex flex-row items-center space-x-[6px] text-[13px] font-medium leading-[21px] px-[15px] py-[7px] select-none rounded-md`,
				textColorMap[type],
				bgColorMap[type],
				borderMap[type],
				className,
				iconOnly && "min-w-fit",
				!!trailing && "pr-[10px]",
			)}
			title={label} // Recovers the full text when the pill is icon-only or the label ellipses
		>
			{iconType && (
				<Icon type={iconType || "book-open"} size="mini" className={textColorMap[type]} />
			)}
			{/* `min-w-0` so the label is allowed to shrink — a flex item's default `min-width: auto`
			    would otherwise let a nowrap label overrun its siblings instead of ellipsing. */}
			{!iconOnly && (
				<span className={cn(truncateLabel ? "min-w-0 truncate" : "whitespace-nowrap")}>
					{label}
				</span>
			)}
			{trailing}
		</div>
	);
};
const baseClassName = "min-w-full justify-center";

export const ReviewStatusCard = ({
	className,
	iconOnly,
	trailing,
	truncateLabel,
}: {
	className?: string;
	iconOnly?: boolean;
	trailing?: ReactNode;
	truncateLabel?: boolean;
}) => (
	<StatusCard
		type="review"
		label="Underwriter Review"
		className={cn(baseClassName, className)}
		iconOnly={iconOnly}
		trailing={trailing}
		truncateLabel={truncateLabel}
	/>
);

export const ApprovedStatusCard = ({
	className,
	iconOnly,
	trailing,
	truncateLabel,
}: {
	className?: string;
	iconOnly?: boolean;
	trailing?: ReactNode;
	truncateLabel?: boolean;
}) => (
	<StatusCard
		type="approved"
		label="Approved"
		className={cn(baseClassName, className)}
		iconOnly={iconOnly}
		trailing={trailing}
		truncateLabel={truncateLabel}
	/>
);

export const AcceptedStatusCard = ({
	className,
	iconOnly,
	trailing,
	truncateLabel,
}: {
	className?: string;
	iconOnly?: boolean;
	trailing?: ReactNode;
	truncateLabel?: boolean;
}) => (
	<StatusCard
		type="accepted"
		label="Terms Accepted"
		className={cn(baseClassName, className)}
		iconOnly={iconOnly}
		trailing={trailing}
		truncateLabel={truncateLabel}
	/>
);

export const DeclinedStatusCard = ({
	className,
	iconOnly,
	trailing,
	truncateLabel,
}: {
	className?: string;
	iconOnly?: boolean;
	trailing?: ReactNode;
	truncateLabel?: boolean;
}) => (
	<StatusCard
		type="declined"
		label="Declined"
		className={cn(baseClassName, className)}
		iconOnly={iconOnly}
		trailing={trailing}
		truncateLabel={truncateLabel}
	/>
);

export const ActiveStatusCard = ({
	className,
	iconOnly,
	trailing,
	truncateLabel,
}: {
	className?: string;
	iconOnly?: boolean;
	trailing?: ReactNode;
	truncateLabel?: boolean;
}) => (
	<StatusCard
		type="active"
		label="Active"
		className={cn(baseClassName, className)}
		iconOnly={iconOnly}
		trailing={trailing}
		truncateLabel={truncateLabel}
	/>
);

export const PendingStatusCard = ({
	className,
	iconOnly,
	trailing,
	truncateLabel,
}: {
	className?: string;
	iconOnly?: boolean;
	trailing?: ReactNode;
	truncateLabel?: boolean;
}) => (
	<StatusCard
		type="pending"
		label="Pending"
		className={cn(baseClassName, className)}
		iconOnly={iconOnly}
		trailing={trailing}
		truncateLabel={truncateLabel}
	/>
);

export const ClosedStatusCard = ({
	className,
	iconOnly,
	trailing,
	truncateLabel,
}: {
	className?: string;
	iconOnly?: boolean;
	trailing?: ReactNode;
	truncateLabel?: boolean;
}) => (
	<StatusCard
		type="closed"
		label="Closed"
		className={cn(baseClassName, className)}
		iconOnly={iconOnly}
		trailing={trailing}
		truncateLabel={truncateLabel}
	/>
);

const statusMap = (
	status: StatusCardType,
	className?: string,
	options?: { iconOnly?: boolean; trailing?: ReactNode; truncateLabel?: boolean },
) => {
	switch (status) {
		case "review":
			return <ReviewStatusCard className={className} {...options} />;
		case "approved":
			return <ApprovedStatusCard className={className} {...options} />;
		case "accepted":
			return <AcceptedStatusCard className={className} {...options} />;
		case "declined":
			return <DeclinedStatusCard className={className} {...options} />;
		case "active":
			return <ActiveStatusCard className={className} {...options} />;
		case "pending":
			return <PendingStatusCard className={className} {...options} />;
		case "closed":
			return <ClosedStatusCard className={className} {...options} />;
		default:
			return null;
	}
};

export const getStatusCardByStatus = (
	status: StatusCardType,
	className?: string,
	options?: { iconOnly?: boolean; trailing?: ReactNode; truncateLabel?: boolean },
) => statusMap(status, className, options ?? {});
